import type { OperatorLedger } from '../contract/types.js';
import { classOf, type OperatorClass } from './operator-classes.js';

/**
 * The operator ledger.
 *
 * Every operator encountered during extraction is **observed**, and each is
 * either graphics state — which draws nothing — or something that puts marks on
 * the page, in which case it is **consumed** into an entity or **recorded** as a
 * quality issue. Nothing disappears without being counted and located.
 *
 * This is the honest form of "nothing is missing": not that everything is
 * converted, but that nothing vanishes unaccounted for.
 *
 * **`observed` is counted by its own pass and is the figure the others are
 * checked against**, exactly as `totalInk` is in the raster ink ledger. The
 * earlier version had no such figure: `total` was computed as
 * `consumed + recorded`, and `assertReconciled` compared `consumed + recorded`
 * against it — the same expression on both sides, so no input could make it
 * throw and `unaccounted` was exactly zero on every drawing ever converted.
 * Measured before it was replaced: pdf.js reports 135 operators for fixture F01
 * and the ledger knew of 87; on the sample drawing, 913 against 654. No content
 * was being lost — the difference is graphics state plus path operators counted
 * at segment granularity — but nothing checked that, which was the point.
 *
 * `total`, `consumed` and `recorded` keep their meaning as the **segment-level**
 * account: one path operator becomes several `lineTo` entries. They are a
 * useful detail and they are not the proof; `observed` and `unclassified` are.
 */
export class Ledger {
  private consumedCount = 0;
  private recordedCount = 0;
  private observedCount = 0;
  private readonly byClass: Record<OperatorClass, number> = {
    state: 0,
    path: 0,
    text: 0,
    image: 0,
    shading: 0,
    xobject: 0,
  };
  private readonly unclassifiedByOperator: Record<string, number> = {};
  private readonly byOperator: Record<string, { consumed: number; recorded: number }> = {};

  /**
   * An operator pdf.js reported, counted before anything is decided about it.
   *
   * Called for every entry in the operator list, including the ones this
   * extractor has nothing to do with. An operator with no classification is the
   * one case that fails the run: it is either a pdf.js version that added
   * something, or a thing nobody here has considered, and both are reasons to
   * stop rather than to carry on and report a figure.
   */
  observe(operator: string): void {
    this.observedCount += 1;
    const kind = classOf(operator);
    if (kind === null) {
      this.unclassifiedByOperator[operator] = (this.unclassifiedByOperator[operator] ?? 0) + 1;
      return;
    }
    this.byClass[kind] += 1;
  }

  private bucket(operator: string): { consumed: number; recorded: number } {
    let b = this.byOperator[operator];
    if (!b) {
      b = { consumed: 0, recorded: 0 };
      this.byOperator[operator] = b;
    }
    return b;
  }

  /** The operator became part of an entity. */
  consume(operator: string, n = 1): void {
    this.consumedCount += n;
    this.bucket(operator).consumed += n;
  }

  /** The operator could not be converted and was recorded as a quality issue. */
  record(operator: string, n = 1): void {
    this.recordedCount += n;
    this.bucket(operator).recorded += n;
  }

  snapshot(): OperatorLedger {
    const byOperator: Record<string, { consumed: number; recorded: number }> = {};
    for (const [k, v] of Object.entries(this.byOperator)) {
      byOperator[k] = { consumed: v.consumed, recorded: v.recorded };
    }
    const unclassifiedOperators: Record<string, number> = { ...this.unclassifiedByOperator };
    let unclassified = 0;
    for (const n of Object.values(unclassifiedOperators)) unclassified += n;

    return {
      observed: this.observedCount,
      byClass: { ...this.byClass },
      unclassified,
      unclassifiedOperators,
      total: this.consumedCount + this.recordedCount,
      consumed: this.consumedCount,
      recorded: this.recordedCount,
      byOperator,
    };
  }

  /**
   * Throws when an operator was read and this extractor has no idea what it is.
   *
   * That is the only condition here that can fail, and it is deliberately the
   * only one: the segment-level counts cannot disagree with a total derived from
   * them, and pretending otherwise is what the previous version did.
   */
  assertReconciled(): void {
    const s = this.snapshot();
    if (s.unclassified > 0) {
      const names = Object.entries(s.unclassifiedOperators)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([name, n]) => `${name} x ${String(n)}`)
        .join(', ');
      throw new Error(
        `operator ledger does not reconcile: ${String(s.unclassified)} of ${String(s.observed)} ` +
          `operators have no classification (${names}). ` +
          'Classify them in operator-classes.ts — an operator nobody has considered is ' +
          'content that would vanish with every figure still reading correct.',
      );
    }
  }
}
